import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardCheck, Plus, Search, X, ChevronDown, AlertTriangle,
  Clock, CheckCircle2, Play, FileText, Download, Trash2, RefreshCw, Upload, Eye, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import { useSociete } from '../context/SocieteContext';

// ─── Utilitaires ─────────────────────────────────────────────────────────────

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDays(n) {
  if (n == null) return '—';
  if (n < 0) return `${Math.abs(n)}j de retard`;
  if (n === 0) return "Aujourd'hui";
  return `dans ${n}j`;
}

const URGENCE_STYLES = {
  overdue: { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA', label: 'En retard', icon: AlertTriangle },
  upcoming: { bg: '#FFF7ED', color: '#EA580C', border: '#FED7AA', label: 'À venir', icon: Clock },
  ok: { bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0', label: 'Planifié', icon: CheckCircle2 },
};

const STATUT_STYLES = {
  en_cours: { bg: '#FFF7ED', color: '#EA580C', label: 'En cours' },
  termine: { bg: '#F0FDF4', color: '#16A34A', label: 'Terminé' },
};

// ─── Composant : zone drag-and-drop PDF ──────────────────────────────────────

function PdfDropzone({ file, onFile, nom, onNomChange }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf') { toast.error('Seuls les fichiers PDF sont acceptés'); return; }
    onFile(f);
    if (!nom) onNomChange(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files[0]);
  };

  if (file) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
          style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
          <FileText size={16} style={{ color: '#16A34A' }} />
          <span className="flex-1 text-sm font-medium truncate" style={{ color: '#16A34A' }}>{file.name}</span>
          <button type="button" onClick={() => { onFile(null); onNomChange(''); }}
            className="rounded-full p-0.5 hover:bg-green-200 transition-colors" title="Retirer le fichier">
            <X size={13} style={{ color: '#16A34A' }} />
          </button>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Nom du document
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={nom}
              onChange={e => onNomChange(e.target.value)}
              data-testid="pj-nom-input"
              placeholder="Nom du rapport..."
              className="flex-1 px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            />
            <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>.pdf</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      data-testid="pdf-dropzone"
      className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all select-none"
      style={{
        borderColor: dragging ? 'var(--primary-solid)' : 'var(--border)',
        background: dragging ? 'var(--primary-50)' : '#FAFAFA',
      }}
    >
      <Upload size={22} className="mx-auto mb-2 transition-colors"
        style={{ color: dragging ? 'var(--primary-solid)' : 'var(--text-muted)' }} />
      <p className="text-sm font-medium transition-colors"
        style={{ color: dragging ? 'var(--primary-solid)' : 'var(--text-muted)',  }}>
        {dragging ? 'Relâcher pour déposer' : 'Glisser-déposer un PDF ici'}
      </p>
      <p className="text-xs mt-1" style={{ color: 'var(--text-disabled)' }}>ou cliquer pour parcourir • max 20 Mo</p>
      <input ref={inputRef} type="file" accept=".pdf" className="hidden"
        onChange={e => pickFile(e.target.files[0])} />
    </div>
  );
}

// ─── Composant : carte stat ───────────────────────────────────────────────────

function StatCard({ title, value, accent, sub, onClick, isActive }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-').replace(/[éè]/g, 'e').replace(/[àâ]/g, 'a')}`}
      className={`bg-white rounded-xl p-4 border transition-all ${clickable ? 'cursor-pointer select-none' : ''}`}
      style={{
        borderColor: isActive ? accent || 'var(--primary-solid)' : 'var(--border)',
        boxShadow: isActive ? `0 0 0 2px ${accent || 'var(--primary-solid)'}22` : undefined,
        background: isActive ? `${accent || 'var(--primary-solid)'}08` : 'white',
      }}
    >
      <p className="text-xs font-semibold uppercase tracking-wide mb-1"
        style={{ color: isActive ? accent || 'var(--primary-solid)' : 'var(--text-muted)',  }}>
        {title}
      </p>
      <p className="text-2xl font-bold" style={{ color: isActive ? accent || 'var(--primary-solid)' : accent || 'var(--text)',  }}>
        {value}
      </p>
      {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
    </div>
  );
}

// ─── Composant : multi-sélect avec recherche ──────────────────────────────────

function MultiSelectField({ label, placeholder, items, selectedIds, onChange, searchable = false, onSearch, searchLoading = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = searchable
    ? items
    : items.filter(it => it.label.toLowerCase().includes(q.toLowerCase()));

  const handleInput = (val) => {
    setQ(val);
    if (searchable && onSearch) onSearch(val);
  };

  const toggle = (id) => {
    const next = selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id];
    onChange(next);
  };

  const selectedItems = items.filter(it => selectedIds.includes(it.id));

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>{label}</label>

      {/* Tags sélectionnés */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedItems.map(it => (
            <span key={it.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' }}>
              {it.label}
              <button type="button" onClick={() => toggle(it.id)} className="hover:opacity-70">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input déclencheur */}
      <div
        className="w-full px-3 py-2 rounded-lg border text-sm flex items-center gap-2 cursor-pointer"
        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
        onClick={() => setOpen(v => !v)}
      >
        <Search size={13} />
        <input
          type="text"
          value={q}
          onChange={e => handleInput(e.target.value)}
          onClick={e => { e.stopPropagation(); setOpen(true); }}
          placeholder={placeholder}
          className="flex-1 outline-none bg-transparent text-sm"
          style={{ color: 'var(--text)' }}
        />
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border shadow-lg max-h-52 overflow-y-auto"
          style={{ borderColor: 'var(--border)' }}>
          {searchLoading && (
            <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>Recherche...</div>
          )}
          {!searchLoading && filtered.length === 0 && (
            <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>Aucun résultat</div>
          )}
          {filtered.map(it => (
            <button
              key={it.id}
              type="button"
              onClick={() => toggle(it.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
              style={{ color: selectedIds.includes(it.id) ? 'var(--primary-solid)' : 'var(--text)' }}
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selectedIds.includes(it.id) ? 'border-blue-600' : ''}`}
                style={{ background: selectedIds.includes(it.id) ? 'var(--primary-solid)' : 'white', borderColor: selectedIds.includes(it.id) ? 'var(--primary-solid)' : 'var(--border)' }}>
                {selectedIds.includes(it.id) && <X size={9} className="text-white" />}
              </div>
              <span className="flex-1 text-left">{it.label}</span>
              {it.sub && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{it.sub}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Constantes Parc (identique à Materiels.js) ──────────────────────────────

const PARCS_OPTIONS = [
  { id: 'location_courte_duree', label: 'Location C.D.' },
  { id: 'client',                label: 'Client' },
  { id: 'stock',                 label: 'Stock' },
  { id: 'vehicule_de_societe',   label: 'Véh. société' },
  { id: 'epave',                 label: 'Épave' },
];

// ─── Onglet 1 : À effectuer ───────────────────────────────────────────────────

function AEffectuerTab({ onRefresh }) {
  const { activeAgences } = useSociete();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [filterUrgence, setFilterUrgence] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getControlesAEffectuer();
      setItems(res.data);
    } catch {
      toast.error('Erreur lors du chargement des contrôles à effectuer');
    } finally {
      setLoading(false);
    }
  }, [activeAgences]);

  useEffect(() => { load(); }, [load]);

  const handleDemarrer = async (item) => {
    setStarting(`${item.ct_id}-${item.materiel_id}`);
    try {
      await api.demarrerControle({ ct_id: item.ct_id, materiel_id: item.materiel_id });
      toast.success(`Contrôle démarré pour ${item.materiel_nom}`);
      load();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du démarrage');
    } finally {
      setStarting(null);
    }
  };

  const overdue = items.filter(i => i.urgence === 'overdue');
  const upcoming = items.filter(i => i.urgence === 'upcoming');

  const displayed = filterUrgence === 'all' ? items
    : filterUrgence === 'overdue' ? overdue
    : upcoming;

  return (
    <div className="space-y-4">
      {/* Stat cards filtrantes */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          title="En retard" value={overdue.length} accent="#DC2626"
          onClick={() => setFilterUrgence(f => f === 'overdue' ? 'all' : 'overdue')}
          isActive={filterUrgence === 'overdue'}
        />
        <StatCard
          title="À venir" value={upcoming.length} accent="#EA580C"
          onClick={() => setFilterUrgence(f => f === 'upcoming' ? 'all' : 'upcoming')}
          isActive={filterUrgence === 'upcoming'}
        />
        <StatCard
          title="Total à traiter" value={items.length} accent="var(--primary-solid)"
          onClick={() => setFilterUrgence('all')}
          isActive={filterUrgence === 'all'}
        />
        <div
          className="bg-white rounded-xl p-4 border cursor-pointer hover:shadow-md transition-all"
          style={{ borderColor: 'var(--border)' }}
          onClick={load}
          data-testid="stat-card-actualiser"
        >
          <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)',  }}>Actualiser</p>
          <RefreshCw size={22} className={loading ? 'animate-spin' : ''} style={{ color: 'var(--primary-solid)' }} />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : displayed.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: '#22C55E' }} />
          <p className="font-semibold" style={{ color: 'var(--text)',  }}>
            {filterUrgence === 'all' ? 'Aucun contrôle à effectuer' : 'Aucun contrôle dans cette catégorie'}
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Tout est à jour !</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: '#FAFAFA' }}>
                  {['Matériel', 'N° Parc', 'N° Immat', 'N° Série', 'Type de contrôle', 'Dernier contrôle', 'Prochain prévu', 'Échéance', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'var(--text-muted)',  }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map((item, idx) => {
                  const style = URGENCE_STYLES[item.urgence] || URGENCE_STYLES.ok;
                  const key = `${item.ct_id}-${item.materiel_id}`;
                  const isStarting = starting === key;
                  return (
                    <tr key={key}
                      style={{
                        borderBottom: idx < displayed.length - 1 ? '1px solid var(--border-light)' : 'none',
                        background: style.bg,
                      }}>
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)',  }}>
                        <span>{item.materiel_nom}</span>
                        {(item.materiel_marque || item.materiel_modele) && (
                          <span className="text-xs font-normal ml-1" style={{ color: 'var(--text-muted)' }}>
                            {[item.materiel_marque, item.materiel_modele].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{item.numero_parc || '—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{item.immatriculation || '—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{item.numero_serie || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                          {item.ct_nom}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{fmtDate(item.last_date)}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{fmtDate(item.next_date)}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: style.color }}>
                          <style.icon size={13} />
                          {fmtDays(item.days_until)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`start-controle-${key}`}
                          onClick={() => handleDemarrer(item)}
                          disabled={isStarting}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                          style={{ background: 'var(--primary-gradient)' }}
                        >
                          <Play size={12} />
                          {isStarting ? 'Démarrage...' : 'Démarrer'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Panneau : terminer / éditer une réalisation ──────────────────────────────

function RealisationPanel({ realisation, onClose, onSaved, onDelete }) {
  const [dateRealisation, setDateRealisation] = useState(realisation.date_realisation?.slice(0, 10) || '');
  const [dateDebut, setDateDebut] = useState(realisation.date_debut?.slice(0, 10) || '');
  const [numeroControle, setNumeroControle] = useState(realisation.numero_controle || '');
  const [notes, setNotes] = useState(realisation.notes || '');
  const [file, setFile] = useState(null);
  const [nomPj, setNomPj] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingPartial, setSavingPartial] = useState(false);

  const isTermine = realisation.statut === 'termine';
  const statStyle = STATUT_STYLES[realisation.statut] || STATUT_STYLES.en_cours;

  const handleSave = async () => {
    setSavingPartial(true);
    try {
      const payload = { notes };
      if (dateDebut) payload.date_debut = dateDebut;
      if (numeroControle) payload.numero_controle = numeroControle;
      await api.updateControleRealisation(realisation.cr_id, payload);

      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        if (nomPj.trim()) formData.append('nom_pj', nomPj.trim());
        await api.uploadPjControle(realisation.cr_id, formData);
        setFile(null);
        setNomPj('');
      }

      toast.success('Informations enregistrées');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingPartial(false);
    }
  };

  const handleTerminer = async () => {
    if (!dateRealisation) { toast.error('La date de réalisation est requise'); return; }
    setSaving(true);
    try {
      // Sauvegarder le numéro de contrôle avant de clôturer
      if (numeroControle) {
        await api.updateControleRealisation(realisation.cr_id, { numero_controle: numeroControle });
      }
      const formData = new FormData();
      formData.append('date_realisation', dateRealisation);
      if (notes) formData.append('notes', notes);
      if (file) {
        formData.append('file', file);
        if (nomPj.trim()) formData.append('nom_pj', nomPj.trim());
      }
      await api.terminerControle(realisation.cr_id, formData);
      toast.success('Contrôle terminé avec succès');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la clôture');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await api.downloadPjControle(realisation.cr_id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${realisation.pj_nom || 'rapport'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleView = async () => {
    try {
      const res = await api.downloadPjControle(realisation.cr_id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      // Révoquer après 30s (le temps que le nouvel onglet charge le PDF)
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Impossible d\'ouvrir le PDF');
    }
  };

  return (
    <div className="space-y-5">
      {/* Infos matériel enrichies */}
      <div className="p-4 rounded-xl" style={{ background: 'var(--primary-50)', border: '1px solid var(--primary-100)' }}>
        <p className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Matériel</p>
        {/* Ligne 1 : nom · marque modèle */}
        <p className="font-semibold text-sm leading-snug" style={{ color: 'var(--text)',  }}>
          {realisation.materiel_nom}
          {(realisation.materiel_marque || realisation.materiel_modele) && (
            <span className="font-normal text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
              · {[realisation.materiel_marque, realisation.materiel_modele].filter(Boolean).join(' ')}
            </span>
          )}
        </p>
        {/* Ligne 2 : N° parc · immat · N° série */}
        {(realisation.numero_parc || realisation.materiel_immatriculation || realisation.materiel_numero_serie) && (
          <p className="text-xs mt-1 flex flex-wrap gap-x-3" style={{ color: 'var(--text-muted)' }}>
            {realisation.numero_parc && <span>Parc&nbsp;<strong style={{ color: 'var(--text)' }}>{realisation.numero_parc}</strong></span>}
            {realisation.materiel_immatriculation && <span>Immat&nbsp;<strong style={{ color: 'var(--text)' }}>{realisation.materiel_immatriculation}</strong></span>}
            {realisation.materiel_numero_serie && <span>N° série&nbsp;<strong style={{ color: 'var(--text)' }}>{realisation.materiel_numero_serie}</strong></span>}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Type de contrôle</p>
          <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{realisation.ct_nom}</p>
        </div>
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Statut</p>
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: statStyle.bg, color: statStyle.color }}>
              {statStyle.label}
            </span>
          </div>
          {onDelete && (
            <button
              data-testid="panel-delete-realisation-btn"
              onClick={onDelete}
              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors mt-0.5"
              title="Supprimer ce contrôle"
            >
              <Trash2 size={14} style={{ color: '#DC2626' }} />
            </button>
          )}
        </div>
      </div>

      {/* Date début */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Date de début</label>
        <input
          type="date"
          value={dateDebut}
          onChange={e => setDateDebut(e.target.value)}
          disabled={isTermine}
          className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
        />
      </div>

      {/* Date réalisation */}
      {!isTermine && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Date de réalisation <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <input
            type="date"
            value={dateRealisation}
            onChange={e => setDateRealisation(e.target.value)}
            data-testid="date-realisation-input"
            className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          />
        </div>
      )}
      {isTermine && realisation.date_realisation && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Date de réalisation</p>
          <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{fmtDate(realisation.date_realisation)}</p>
        </div>
      )}

      {/* Numéro de contrôle */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Numéro de contrôle</label>
        {!isTermine ? (
          <input
            type="text"
            value={numeroControle}
            onChange={e => setNumeroControle(e.target.value)}
            data-testid="numero-controle-input"
            placeholder="Ex : VGP-2026-001"
            className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          />
        ) : (
          realisation.numero_controle && (
            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{realisation.numero_controle}</p>
          )
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          disabled={isTermine}
          rows={3}
          className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100 resize-none disabled:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          placeholder="Observations..."
        />
      </div>

      {/* Rapport existant (affiché dès qu'un PDF est présent, quel que soit le statut) */}
      {realisation.pj_nom && (
        <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={14} style={{ color: '#16A34A', flexShrink: 0 }} />
            <span className="text-sm font-medium truncate" style={{ color: '#16A34A' }}>{realisation.pj_nom}.pdf</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            <button
              data-testid="view-pj-btn"
              onClick={handleView}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: '#16A34A' }}
              title="Ouvrir le PDF dans un nouvel onglet"
            >
              <Eye size={12} />
              Consulter
            </button>
            <button
              data-testid="download-pj-btn"
              onClick={handleDownload}
              className="p-1.5 rounded-lg border transition-colors hover:bg-green-50"
              style={{ borderColor: '#BBF7D0', color: '#16A34A' }}
              title="Télécharger le PDF"
            >
              <Download size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Upload rapport PDF (uniquement si pas encore de PDF et contrôle en cours) */}
      {!isTermine && !realisation.pj_nom && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Rapport PDF (optionnel)</label>
          <PdfDropzone file={file} onFile={setFile} nom={nomPj} onNomChange={setNomPj} />
        </div>
      )}

      {/* Actions */}
      {!isTermine && (
        <div className="flex gap-2">
          <button
            data-testid="save-realisation-btn"
            onClick={handleSave}
            disabled={savingPartial || saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
            style={{ border: '1.5px solid var(--primary-solid)', color: 'var(--primary-solid)', background: 'white' }}
          >
            {savingPartial ? 'Enregistrement...' : 'Enregistrer'}
          </button>
          <button
            data-testid="terminer-controle-btn"
            onClick={handleTerminer}
            disabled={saving || savingPartial || !dateRealisation}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
            style={{ background: 'var(--primary-gradient)' }}
          >
            {saving ? 'Clôture...' : 'Terminer le contrôle'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Panneau : nouveau contrôle ───────────────────────────────────────────────

function NewRealisationPanel({ onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [newCt, setNewCt] = useState('');
  const [newMatSearch, setNewMatSearch] = useState('');
  const [newMatItems, setNewMatItems] = useState([]);
  const [newMat, setNewMat] = useState(null);
  const [newDateDebut, setNewDateDebut] = useState(new Date().toISOString().slice(0, 10));
  const [newDateReal, setNewDateReal] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfNom, setPdfNom] = useState('');
  const [saving, setSaving] = useState(false);

  // Charger les types de contrôle au montage du panneau
  useEffect(() => {
    api.getControleTypes()
      .then(res => setTypes(res.data))
      .catch(() => toast.error('Impossible de charger les types de contrôle'));
  }, []);

  useEffect(() => {
    if (newMatSearch.length < 2) { setNewMatItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.getMateriels({ slim: true, search: newMatSearch });
        setNewMatItems(res.data.slice(0, 20).map(m => ({
          id: m.materiel_id,
          label: m.nom,
          sub: [m.numero_parc, m.immatriculation, m.numero_serie].filter(Boolean).join(' · '),
        })));
      } catch { setNewMatItems([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [newMatSearch]);

  const handleCreate = async () => {
    if (!newCt || !newMat) { toast.error('Sélectionnez un type et un matériel'); return; }
    if (pdfFile && !newDateReal) { toast.error('La date de réalisation est requise pour joindre un rapport'); return; }
    setSaving(true);
    try {
      const created = await api.createControleRealisation({
        ct_id: newCt,
        materiel_id: newMat.id,
        date_debut: newDateDebut,
        date_realisation: newDateReal || null,
        notes: newNotes || null,
      });
      // Si un PDF est joint, appeler terminer pour l'attacher
      if (pdfFile && newDateReal) {
        const formData = new FormData();
        formData.append('date_realisation', newDateReal);
        if (newNotes) formData.append('notes', newNotes);
        formData.append('file', pdfFile);
        if (pdfNom.trim()) formData.append('nom_pj', pdfNom.trim());
        await api.terminerControle(created.data.cr_id, formData);
      }
      toast.success('Contrôle créé');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Type */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Type de contrôle *</label>
        <select
          value={newCt}
          onChange={e => setNewCt(e.target.value)}
          data-testid="new-ct-select"
          className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
          style={{ borderColor: 'var(--border)', color: newCt ? 'var(--text)' : 'var(--text-muted)' }}
        >
          <option value="">Choisir un type...</option>
          {types.map(t => <option key={t.ct_id} value={t.ct_id}>{t.nom}</option>)}
        </select>
      </div>

      {/* Matériel */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Matériel *</label>
        {newMat ? (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border" style={{ borderColor: 'var(--primary-solid)', background: 'var(--primary-50)' }}>
            <span className="flex-1 text-sm font-medium" style={{ color: 'var(--text)' }}>{newMat.label}</span>
            {newMat.sub && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{newMat.sub}</span>}
            <button type="button" onClick={() => { setNewMat(null); setNewMatSearch(''); }}>
              <X size={14} style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>
        ) : (
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              value={newMatSearch}
              onChange={e => setNewMatSearch(e.target.value)}
              data-testid="new-mat-search"
              placeholder="Rechercher par nom, N° parc, immatriculation..."
              className="w-full pl-8 pr-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            />
            {newMatItems.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border shadow-lg max-h-48 overflow-y-auto" style={{ borderColor: 'var(--border)' }}>
                {newMatItems.map(m => (
                  <button key={m.id} type="button"
                    onClick={() => { setNewMat(m); setNewMatSearch(''); setNewMatItems([]); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left"
                    style={{ color: 'var(--text)' }}>
                    <span className="flex-1">{m.label}</span>
                    {m.sub && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{m.sub}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Date de début</label>
          <input type="date" value={newDateDebut} onChange={e => setNewDateDebut(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Date de réalisation</label>
          <input type="date" value={newDateReal} onChange={e => setNewDateReal(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Laisser vide si en cours</p>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Notes</label>
        <textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} rows={3}
          className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100 resize-none"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }} placeholder="Observations, remarques..." />
      </div>

      {/* Rapport PDF */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>
          Rapport PDF
          {!newDateReal && <span className="ml-1 font-normal normal-case" style={{ color: 'var(--text-disabled)' }}>(renseigner la date de réalisation pour joindre un rapport)</span>}
        </label>
        {newDateReal
          ? <PdfDropzone file={pdfFile} onFile={setPdfFile} nom={pdfNom} onNomChange={setPdfNom} />
          : (
            <div className="border-2 border-dashed rounded-xl p-5 text-center opacity-40 select-none"
              style={{ borderColor: 'var(--border)', background: '#FAFAFA' }}>
              <Upload size={20} className="mx-auto mb-1" style={{ color: 'var(--text-muted)' }} />
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Disponible après saisie de la date de réalisation</p>
            </div>
          )
        }
      </div>

      {/* Action */}
      <button
        data-testid="save-new-realisation-btn"
        onClick={handleCreate}
        disabled={saving || !newCt || !newMat}
        className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
        style={{ background: 'var(--primary-gradient)' }}
      >
        {saving ? 'Enregistrement...' : 'Créer le contrôle'}
      </button>
    </div>
  );
}

// ─── Onglet 2 : Contrôles effectués ──────────────────────────────────────────

function EffectuesTab({ types, refreshKey, isReadOnly }) {
  const { activeAgences } = useSociete();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [newPanelOpen, setNewPanelOpen] = useState(false);
  const selectedCrIdRef = useRef(null);

  // Sync ref with current selected cr_id to avoid stale closure in load()
  useEffect(() => {
    selectedCrIdRef.current = selected?.cr_id || null;
  }, [selected]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getControleRealisations();
      setItems(res.data);
      // Refresh selected item if side panel is open
      if (selectedCrIdRef.current) {
        const updated = res.data.find(i => i.cr_id === selectedCrIdRef.current);
        if (updated) setSelected(updated);
      }
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [activeAgences]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const enCours = items.filter(i => i.statut === 'en_cours').length;
  const termines = items.filter(i => i.statut === 'termine').length;

  const bySearch = search
    ? items.filter(i => {
        const q = search.toLowerCase();
        return (
          (i.materiel_nom || '').toLowerCase().includes(q) ||
          (i.materiel_marque || '').toLowerCase().includes(q) ||
          (i.materiel_modele || '').toLowerCase().includes(q) ||
          (i.ct_nom || '').toLowerCase().includes(q) ||
          (i.numero_parc || '').toLowerCase().includes(q) ||
          (i.materiel_immatriculation || '').toLowerCase().includes(q) ||
          (i.materiel_numero_serie || '').toLowerCase().includes(q) ||
          (i.numero_controle || '').toLowerCase().includes(q)
        );
      })
    : items;

  const filtered = filterStatut === 'all' ? bySearch
    : bySearch.filter(i => i.statut === filterStatut);

  const handleDelete = async (crId) => {
    if (!window.confirm('Supprimer définitivement ce contrôle ?')) return;
    try {
      await api.deleteControleRealisation(crId);
      toast.success('Contrôle supprimé');
      setPanelOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-4">
      {/* Stat cards filtrantes */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          title="En cours" value={enCours} accent="#EA580C"
          onClick={() => setFilterStatut(f => f === 'en_cours' ? 'all' : 'en_cours')}
          isActive={filterStatut === 'en_cours'}
        />
        <StatCard
          title="Terminés" value={termines} accent="#16A34A"
          onClick={() => setFilterStatut(f => f === 'termine' ? 'all' : 'termine')}
          isActive={filterStatut === 'termine'}
        />
        <StatCard
          title="Total" value={items.length} accent="var(--primary-solid)"
          onClick={() => setFilterStatut('all')}
          isActive={filterStatut === 'all'}
        />
      </div>

      {/* Barre outils */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-48 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="search-effectues"
            placeholder="Rechercher un matériel, type..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          />
        </div>
        <button
          data-testid="add-realisation-btn"
          onClick={() => setNewPanelOpen(true)}
          disabled={isReadOnly}
          title={isReadOnly ? 'Sélectionnez une seule agence pour créer un contrôle' : undefined}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary-gradient)' }}
        >
          <Plus size={15} />
          Nouveau contrôle
        </button>
      </div>

      {/* SidePanel nouveau contrôle */}
      <SidePanel
        open={newPanelOpen}
        onOpenChange={setNewPanelOpen}
        title="Nouveau contrôle"
        subtitle="Créer un contrôle manuellement"
      >
        <NewRealisationPanel
          onClose={() => setNewPanelOpen(false)}
          onSaved={load}
        />
      </SidePanel>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <ClipboardCheck size={40} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="font-semibold" style={{ color: 'var(--text)',  }}>Aucun contrôle enregistré</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Démarrez un contrôle depuis le planificateur ou créez-en un manuellement.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: '#FAFAFA' }}>
                  {['Matériel', 'N° Parc', 'N° Immat', 'N° Série', 'Type de contrôle', 'N° Contrôle', 'Date début', 'Date réalisation', 'Statut', 'Rapport'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'var(--text-muted)',  }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => {
                  const ss = STATUT_STYLES[item.statut] || STATUT_STYLES.en_cours;
                  return (
                    <tr
                      key={item.cr_id}
                      data-testid={`realisation-row-${item.cr_id}`}
                      onClick={() => { setSelected(item); setPanelOpen(true); }}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ borderBottom: idx < filtered.length - 1 ? '1px solid var(--border-light)' : 'none' }}
                    >
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)',  }}>
                        <span>{item.materiel_nom}</span>
                        {(item.materiel_marque || item.materiel_modele) && (
                          <span className="text-xs font-normal ml-1" style={{ color: 'var(--text-muted)' }}>
                            {[item.materiel_marque, item.materiel_modele].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {item.numero_parc || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {item.materiel_immatriculation || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {item.materiel_numero_serie || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                          {item.ct_nom}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {item.numero_controle || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{fmtDate(item.date_debut)}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{fmtDate(item.date_realisation)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: ss.bg, color: ss.color }}>
                          {ss.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.pj_nom
                          ? <span className="flex items-center gap-1 text-xs" style={{ color: '#16A34A' }}><FileText size={12} />{item.pj_nom}</span>
                          : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Side panel */}
      {selected && (
        <SidePanel
          open={panelOpen}
          onOpenChange={setPanelOpen}
          title={selected.ct_nom}
          subtitle={selected.materiel_nom}
        >
          <RealisationPanel
            realisation={selected}
            onClose={() => setPanelOpen(false)}
            onSaved={load}
            onDelete={() => handleDelete(selected.cr_id)}
          />
        </SidePanel>
      )}
    </div>
  );
}

// ─── Panneau : type de contrôle ───────────────────────────────────────────────

function TypeControlPanel({ type, onClose, onSaved }) {
  const [exploitants, setExploitants] = useState([]);

  const [form, setForm] = useState({
    nom: type?.nom || '',
    recurrence_jours: type?.recurrence_jours || 365,
    preavis_jours: type?.preavis_jours || 30,
    famille_ids: type?.famille_ids || [],
    sous_famille_ids: type?.sous_famille_ids || [],
    modele_ids: type?.modele_ids || [],
    materiel_ids: type?.materiel_ids || [],
    exploitant_ids: type?.exploitant_ids || [],
    parc_values: type?.parc_values || [],
  });

  const [familles, setFamilles] = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [matSearch, setMatSearch] = useState('');
  const [matItems, setMatItems] = useState([]);
  const [selectedMats, setSelectedMats] = useState([]); // full objects for display
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Charger les listes au montage
  useEffect(() => {
    Promise.all([
      api.getFamilles({ type: 'materiel' }),
      api.getSousFamilles(),
      api.getModeles(),
      api.getExploitantsListe(),
    ]).then(([f, sf, m, ex]) => {
      setFamilles(f.data.map(x => ({ id: x.famille_id, label: x.nom })));
      setSousFamilles(sf.data.map(x => ({ id: x.sous_famille_id, label: x.nom })));
      setModeles(m.data.map(x => ({ id: x.modele_id, label: x.nom, sub: x.marque_nom || '' })));
      setExploitants(Array.isArray(ex.data) ? ex.data.map(x => ({ id: x.exploitant_id, label: x.exploitant_nom, sub: x.exploitant_type === 'personne' ? 'Personne' : 'Entreprise' })) : []);
    }).catch(() => {});
  }, []);

  // Charger les matériels déjà sélectionnés (par IDs uniquement, pas toute la liste)
  useEffect(() => {
    if (type?.materiel_ids?.length) {
      Promise.all(
        type.materiel_ids.map(id =>
          api.getMateriel(id)
            .then(r => r.data)
            .catch(() => null)
        )
      ).then(results => {
        const selected = results
          .filter(Boolean)
          .map(m => ({ id: m.materiel_id, label: m.nom, sub: m.numero_parc || m.immatriculation || '' }));
        setSelectedMats(selected);
      });
    }
  }, [type]);

  // Recherche matériels
  useEffect(() => {
    if (matSearch.length < 2) { setMatItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.getMateriels({ slim: true, search: matSearch });
        setMatItems(res.data.slice(0, 20).map(m => ({
          id: m.materiel_id,
          label: m.nom,
          sub: [m.numero_parc, m.immatriculation, m.numero_serie, m.modele].filter(Boolean).join(' · '),
        })));
      } catch { setMatItems([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [matSearch]);

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      const payload = { ...form, materiel_ids: form.materiel_ids };
      if (type?.ct_id) {
        await api.updateControleType(type.ct_id, payload);
        toast.success('Type mis à jour');
      } else {
        await api.createControleType(payload);
        toast.success('Type créé');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer le type "${form.nom}" ?`)) return;
    setDeleting(true);
    try {
      await api.deleteControleType(type.ct_id);
      toast.success('Type supprimé');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setDeleting(false);
    }
  };

  const toggleMat = (item) => {
    const exists = form.materiel_ids.includes(item.id);
    if (exists) {
      setForm(f => ({ ...f, materiel_ids: f.materiel_ids.filter(x => x !== item.id) }));
      setSelectedMats(s => s.filter(x => x.id !== item.id));
    } else {
      setForm(f => ({ ...f, materiel_ids: [...f.materiel_ids, item.id] }));
      setSelectedMats(s => [...s, item]);
    }
  };

  return (
    <div className="space-y-5">
      {/* Nom */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Nom du type *</label>
        <input
          type="text"
          value={form.nom}
          onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
          data-testid="ct-nom-input"
          placeholder="Ex: VGP annuel, Contrôle hydraulique..."
          className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
        />
      </div>

      {/* Récurrence & Préavis */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Récurrence (jours) *</label>
          <input
            type="number"
            value={form.recurrence_jours}
            onChange={e => setForm(f => ({ ...f, recurrence_jours: parseInt(e.target.value) || 0 }))}
            data-testid="ct-recurrence-input"
            min={1}
            className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          />
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            {form.recurrence_jours ? `≈ ${Math.round(form.recurrence_jours / 30.4)} mois` : ''}
          </p>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Préavis (jours) *</label>
          <input
            type="number"
            value={form.preavis_jours}
            onChange={e => setForm(f => ({ ...f, preavis_jours: parseInt(e.target.value) || 0 }))}
            data-testid="ct-preavis-input"
            min={0}
            className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          />
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Alerte {form.preavis_jours}j avant l'échéance</p>
        </div>
      </div>

      {/* Séparateur */}
      <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
        <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>Cibles du contrôle</p>
        <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
          Les matériels sont ciblés par l'union de tous les critères sélectionnés (famille OU sous-famille OU modèle OU matériel spécifique).
        </p>
      </div>

      {/* Exploitants */}
      <MultiSelectField
        label="Exploitants"
        placeholder="Sélectionner un exploitant..."
        items={exploitants}
        selectedIds={form.exploitant_ids}
        onChange={ids => setForm(f => ({ ...f, exploitant_ids: ids }))}
      />

      {/* Parc */}
      <MultiSelectField
        label="Parc"
        placeholder="Sélectionner un parc..."
        items={PARCS_OPTIONS}
        selectedIds={form.parc_values}
        onChange={ids => setForm(f => ({ ...f, parc_values: ids }))}
      />

      {/* Familles */}
      <MultiSelectField
        label="Familles"
        placeholder="Rechercher une famille..."
        items={familles}
        selectedIds={form.famille_ids}
        onChange={ids => setForm(f => ({ ...f, famille_ids: ids }))}
      />

      {/* Sous-familles */}
      <MultiSelectField
        label="Sous-familles"
        placeholder="Rechercher une sous-famille..."
        items={sousFamilles}
        selectedIds={form.sous_famille_ids}
        onChange={ids => setForm(f => ({ ...f, sous_famille_ids: ids }))}
      />

      {/* Modèles */}
      <MultiSelectField
        label="Modèles"
        placeholder="Rechercher un modèle..."
        items={modeles}
        selectedIds={form.modele_ids}
        onChange={ids => setForm(f => ({ ...f, modele_ids: ids }))}
      />

      {/* Matériels spécifiques */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Matériels spécifiques</label>
        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Recherche sur toutes les caractéristiques : nom, N° parc, immatriculation, N° série, modèle...</p>

        {/* Tags sélectionnés */}
        {selectedMats.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedMats.map(m => (
              <span key={m.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' }}>
                {m.label}
                {m.sub && <span className="opacity-60">· {m.sub}</span>}
                <button type="button" onClick={() => toggleMat(m)} className="hover:opacity-70"><X size={10} /></button>
              </span>
            ))}
          </div>
        )}

        {/* Recherche */}
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={matSearch}
            onChange={e => setMatSearch(e.target.value)}
            data-testid="mat-search-input"
            placeholder="Rechercher et ajouter des matériels..."
            className="w-full pl-8 pr-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          />
          {matItems.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border shadow-lg max-h-52 overflow-y-auto" style={{ borderColor: 'var(--border)' }}>
              {matItems.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { toggleMat(m); setMatSearch(''); setMatItems([]); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                  style={{ color: form.materiel_ids.includes(m.id) ? 'var(--primary-solid)' : 'var(--text)' }}
                >
                  <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center`}
                    style={{ background: form.materiel_ids.includes(m.id) ? 'var(--primary-solid)' : 'white', borderColor: form.materiel_ids.includes(m.id) ? 'var(--primary-solid)' : 'var(--border)' }}>
                    {form.materiel_ids.includes(m.id) && <X size={9} className="text-white" />}
                  </div>
                  <span className="flex-1 text-left">{m.label}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{m.sub}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          data-testid="save-ct-btn"
          onClick={handleSave}
          disabled={saving || !form.nom.trim()}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
          style={{ background: 'var(--primary-gradient)' }}
        >
          {saving ? 'Enregistrement...' : type?.ct_id ? 'Mettre à jour' : 'Créer le type'}
        </button>
        {type?.ct_id && (
          <button
            data-testid="delete-ct-btn"
            onClick={handleDelete}
            disabled={deleting}
            className="p-2.5 rounded-xl transition-colors hover:bg-red-50"
            style={{ border: '1px solid #FCA5A5', color: '#DC2626' }}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Onglet 3 : Types de contrôle ────────────────────────────────────────────

function TypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [duplicating, setDuplicating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getControleTypes();
      setTypes(res.data);
    } catch {
      toast.error('Erreur lors du chargement des types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setSelected(null); setShowNew(true); setPanelOpen(true); };
  const openEdit = (t) => { setSelected(t); setShowNew(false); setPanelOpen(true); };

  const toggleCheck = (e, id) => {
    e.stopPropagation();
    setCheckedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = (e) => {
    e.stopPropagation();
    setCheckedIds(prev => prev.length === types.length ? [] : types.map(t => t.ct_id));
  };

  const handleDuplicate = async () => {
    if (!checkedIds.length) return;
    setDuplicating(true);
    try {
      await Promise.all(checkedIds.map(id => api.dupliquerControleType(id)));
      toast.success(`${checkedIds.length} type(s) dupliqué(s)`);
      setCheckedIds([]);
      await load();
    } catch {
      toast.error('Erreur lors de la duplication');
    } finally {
      setDuplicating(false);
    }
  };

  const countCibles = (t) => {
    return (t.famille_ids?.length || 0) + (t.sous_famille_ids?.length || 0) + (t.modele_ids?.length || 0) + (t.materiel_ids?.length || 0) + (t.exploitant_ids?.length || 0) + (t.parc_values?.length || 0);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{types.length} type(s) de contrôle configuré(s)</p>
        <button
          data-testid="add-ct-btn"
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: 'var(--primary-gradient)' }}
        >
          <Plus size={15} />
          Nouveau type
        </button>
      </div>

      {/* Barre d'actions contextuelle */}
      {checkedIds.length > 0 && (
        <div
          data-testid="ct-action-bar"
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl border"
          style={{ background: 'var(--primary-50)', borderColor: 'var(--primary-200)' }}
        >
          <span className="text-sm font-semibold" style={{ color: 'var(--primary-solid)' }}>
            {checkedIds.length} sélectionné{checkedIds.length > 1 ? 's' : ''}
          </span>
          <div className="flex-1" />
          <button
            data-testid="ct-duplicate-btn"
            onClick={handleDuplicate}
            disabled={duplicating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: 'var(--primary-solid)', color: 'white' }}
          >
            <Copy size={13} />
            {duplicating ? 'Duplication…' : 'Dupliquer'}
          </button>
          <button
            onClick={() => setCheckedIds([])}
            className="p-1.5 rounded-lg hover:bg-white transition-colors"
            style={{ color: 'var(--text-muted)' }}
            title="Désélectionner tout"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : types.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <ClipboardCheck size={40} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="font-semibold" style={{ color: 'var(--text)',  }}>Aucun type de contrôle</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Créez un type pour commencer à planifier des contrôles.</p>
          <button onClick={openNew} className="mt-4 px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            Créer un type
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: '#FAFAFA' }}>
                  {/* Checkbox tout sélectionner */}
                  <th className="px-3 py-3 w-8">
                    <div
                      onClick={toggleAll}
                      className="w-4 h-4 rounded border cursor-pointer flex items-center justify-center transition-colors"
                      style={{
                        borderColor: checkedIds.length === types.length && types.length > 0 ? 'var(--primary-solid)' : 'var(--border)',
                        background: checkedIds.length === types.length && types.length > 0 ? 'var(--primary-solid)' : 'white',
                      }}
                    >
                      {checkedIds.length === types.length && types.length > 0 && (
                        <CheckCircle2 size={10} color="white" strokeWidth={3} />
                      )}
                      {checkedIds.length > 0 && checkedIds.length < types.length && (
                        <div className="w-2 h-0.5 rounded" style={{ background: 'var(--primary-solid)' }} />
                      )}
                    </div>
                  </th>
                  {['Nom', 'Récurrence', 'Préavis', 'Cibles', 'Familles', 'Modèles', 'Exploitants', 'Parc', 'Matériels'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'var(--text-muted)',  }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {types.map((t, idx) => {
                  const isChecked = checkedIds.includes(t.ct_id);
                  return (
                    <tr
                      key={t.ct_id}
                      data-testid={`ct-row-${t.ct_id}`}
                      onClick={() => openEdit(t)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{
                        borderBottom: idx < types.length - 1 ? '1px solid var(--border-light)' : 'none',
                        background: isChecked ? 'var(--primary-50)' : undefined,
                      }}
                    >
                      {/* Checkbox individuelle */}
                      <td className="px-3 py-3 w-8" onClick={e => toggleCheck(e, t.ct_id)}>
                        <div
                          className="w-4 h-4 rounded border flex items-center justify-center transition-colors"
                          style={{
                            borderColor: isChecked ? 'var(--primary-solid)' : 'var(--border)',
                            background: isChecked ? 'var(--primary-solid)' : 'white',
                          }}
                        >
                          {isChecked && <CheckCircle2 size={10} color="white" strokeWidth={3} />}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text)',  }}>{t.nom}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {t.recurrence_jours}j
                        <span className="ml-1 opacity-60">({Math.round(t.recurrence_jours / 30.4)} mois)</span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{t.preavis_jours}j</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background: countCibles(t) === 0 ? '#FEF2F2' : 'var(--primary-50)', color: countCibles(t) === 0 ? '#DC2626' : 'var(--primary-solid)' }}>
                          {countCibles(t) === 0 ? 'Aucune cible' : `${countCibles(t)} cible(s)`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{t.famille_ids?.length || 0} fam.</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{t.modele_ids?.length || 0} mod.</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{(t.exploitant_ids?.length || t.agence_ids?.length || 0)} expl.</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{t.parc_values?.length || 0} parc</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{t.materiel_ids?.length || 0} mat.</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Side panel */}
      <SidePanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        title={showNew ? 'Nouveau type de contrôle' : (selected?.nom || 'Type de contrôle')}
        subtitle={showNew ? 'Définir la récurrence et les cibles' : `Récurrence : ${selected?.recurrence_jours}j`}
      >
        <TypeControlPanel
          type={showNew ? null : selected}
          onClose={() => setPanelOpen(false)}
          onSaved={load}
        />
      </SidePanel>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

const TABS = [
  { key: 'a-effectuer', label: 'Contrôles à effectuer' },
  { key: 'effectues', label: 'Contrôles effectués' },
  { key: 'types', label: 'Types de contrôle' },
];

export default function ControlesPeriodiques() {
  const [activeTab, setActiveTab] = useState('a-effectuer');
  const [types, setTypes] = useState([]);
  const [effectuesRefreshKey, setEffectuesRefreshKey] = useState(0);
  const { isReadOnly, activeAgences } = useSociete();

  useEffect(() => {
    api.getControleTypes()
      .then(res => setTypes(res.data))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--primary-gradient)' }}>
          <ClipboardCheck size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>
            Contrôles périodiques
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Gestion des VGP et contrôles périodiques sur le parc matériel
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className="px-4 py-2.5 text-sm font-medium transition-all relative"
            style={{
              color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
            }}
          >
            {tab.label}
            {activeTab === tab.key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'var(--primary-solid)' }} />
            )}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {activeTab === 'a-effectuer' && (
        <AEffectuerTab
          onRefresh={() => setEffectuesRefreshKey(k => k + 1)}
        />
      )}
      {activeTab === 'effectues' && (
        <EffectuesTab
          types={types}
          refreshKey={effectuesRefreshKey}
          isReadOnly={isReadOnly}
        />
      )}
      {activeTab === 'types' && <TypesTab />}
    </div>
  );
}
